import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import firebase from 'firebase/compat/app';
import { toast } from 'react-toastify';
import { firebaseAuth } from '../../providers/AuthProvider';

const validatePassword = (password) => {
  if (password.length < 12) return 'Password must be at least 12 characters.';
  if (!/[A-Za-z]/.test(password)) return 'Password must include at least one letter.';
  if (!/[0-9]/.test(password)) return 'Password must include at least one number.';
  return null;
};

const ResetPassword = () => {
  const { inputs, setInputs, errors, setErrors, user, handleSignout } =
    useContext(firebaseAuth);
  const [newInvite, setNewInvite] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendStatus, setResendStatus] = useState(null);
  const navigate = useNavigate();
  const useQuery = new URLSearchParams(window.location.search);

  useEffect(() => {
    if (user?.isAuthenticated) handleSignout();
  }, [user, handleSignout]);

  useEffect(() => {
    const newAccount = useQuery.get('new_account');
    setInputs({ confirm_password: '', password: '', new_account: !!newAccount });
    setErrors([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const passwordError = validatePassword(inputs.password || '');
    if (passwordError) {
      setErrors([passwordError]);
      return;
    }
    if (inputs.password !== inputs.confirm_password) {
      setErrors(['Passwords do not match.']);
      return;
    }
    const code = useQuery.get('oobCode');
    try {
      await firebase.auth().confirmPasswordReset(code, inputs.password);
      await firebase.auth().signOut().catch(() => {});
      navigate('/manage/sign-in?pw_update=1');
    } catch (error) {
      if (
        error.code === 'auth/expired-action-code' ||
        error.code === 'auth/invalid-action-code'
      ) {
        setNewInvite(true);
      } else {
        setErrors([error.message]);
      }
    }
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setInputs((prev) => ({ ...prev, [id]: value }));
  };

  const sendResend = async () => {
    if (!resendEmail) return;
    try {
      const fn = firebase.functions().httpsCallable('appV2/forgotPassword');
      await fn(resendEmail);
      toast.success('A new reset email has been sent.');
      setResendStatus('A new reset email has been sent.');
    } catch (err) {
      console.error(err);
      toast.error('Could not send reset email.');
    }
  };

  if (newInvite) {
    return (
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-zinc-900 dark:text-white">
          Link expired or invalid
        </h1>
        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
          Enter your email and we'll send a fresh reset link.
        </p>
        {resendStatus ? (
          <div className="mt-6 rounded-md bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700 ring-1 ring-inset ring-emerald-500/30 dark:text-emerald-300 dark:ring-emerald-500/20">
            {resendStatus}
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            <div>
              <label htmlFor="resendEmail" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">
                Email address
              </label>
              <input
                id="resendEmail"
                type="email"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                className="mt-2 block w-full rounded-md bg-zinc-50 px-3 py-2 text-sm text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-zinc-900 dark:bg-zinc-800/60 dark:text-white dark:ring-white/10 dark:placeholder:text-zinc-500 dark:focus:ring-white"
              />
            </div>
            <button
              type="button"
              onClick={sendResend}
              className="inline-flex w-full items-center justify-center rounded-md bg-zinc-900 px-3 py-2 text-sm font-semibold text-zinc-50 shadow-sm hover:bg-zinc-800 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100"
            >
              Send new link
            </button>
          </div>
        )}
        <div className="mt-6 text-sm">
          <Link to="/manage/sign-in" className="text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white">
            Back to sign-in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-xl font-semibold tracking-tight text-zinc-900 dark:text-white">
        {inputs.new_account ? 'Create your password' : 'Reset your password'}
      </h1>
      <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
        Choose a strong password — at least 12 characters, including letters and numbers.
      </p>
      <form onSubmit={handleSubmit} className="mt-6 space-y-4">
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">
            Password
          </label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            onChange={handleChange}
            className="mt-2 block w-full rounded-md bg-zinc-50 px-3 py-2 text-sm text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-zinc-900 dark:bg-zinc-800/60 dark:text-white dark:ring-white/10 dark:placeholder:text-zinc-500 dark:focus:ring-white"
          />
        </div>
        <div>
          <label
            htmlFor="confirm_password"
            className="block text-sm font-medium text-zinc-700 dark:text-zinc-300"
          >
            Confirm password
          </label>
          <input
            id="confirm_password"
            type="password"
            autoComplete="new-password"
            onChange={handleChange}
            className="mt-2 block w-full rounded-md bg-zinc-50 px-3 py-2 text-sm text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-zinc-900 dark:bg-zinc-800/60 dark:text-white dark:ring-white/10 dark:placeholder:text-zinc-500 dark:focus:ring-white"
          />
        </div>
        <button
          type="submit"
          className="inline-flex w-full items-center justify-center rounded-md bg-zinc-900 px-3 py-2 text-sm font-semibold text-zinc-50 shadow-sm hover:bg-zinc-800 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100"
        >
          {inputs.new_account ? 'Create password' : 'Reset password'}
        </button>
        {errors[0] && (
          <div className="rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-700 ring-1 ring-inset ring-red-500/30 dark:text-red-300 dark:ring-red-500/20">
            {errors[0]}
          </div>
        )}
      </form>
    </div>
  );
};

export default ResetPassword;
